/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'react',
	'mobx-react',
	'authoring-common',
	'@ba/ba-ui-carbon-toolkit',
	'prop-types',
	'classnames',
	'../../../app/nls/StringResources'

], function(
	React,
	MobxReact,
	AuthoringCommon,
	BA_UI_CARBON_TOOLKIT,
	PropTypes,
	cx,
	stringResources
) {

	const { ColorPicker: AC_ColorPicker } = AuthoringCommon;
	const { FlexLayout, Label } = BA_UI_CARBON_TOOLKIT;
	const CONDITIONAL_PALETTE_TYPE = 'ConditionalPalette';
	const NO_COLOR = 'No color';

	class ColorPicker extends React.Component {

		constructor(props) {
			super(props);
			this._defaultColor = '';
			this.state = {
				colors: [],
				hexValue: '',
				favColors: null
			};
		}

		/**
		 * 1. Search for the color in the list of colours from the "getColorsList" callback.
		 * 2. If it is not found then we may be dealing with a custom colour. Attempt to translate the custom color ID into the hex value.
		 *
		 * Also, this is where we set the default colour to be what the colour is when initially rendering the component not what the
		 * colour was at the time of the last save.
		 */
		componentDidMount() {
			this._updateState();
		}

		/**
		 * Update the react state so that the colorPicker re-render with the right color code
		 */
		componentDidUpdate(preProps) {
			if (this.props.item.value !== preProps.item.value) {
				this._updateState();
			}
		}

		_updateState(){
			const uiControl = this._getUiControl();
			Promise.all([this._getColorsList()]).then(([colors]) => {
				const color = this._getColorFromColorList(colors, this._getNormalizedUiControl());
				if (color) {
					this._defaultColor = color.hexValue;
					this.setState({
						colors,
						hexValue: color.hexValue
					});
				}
			});
			if( uiControl.showFavoriteColors) {
				Promise.all(this.props.paletteService.getFavoriteColors()).then((colors) => {
					this.setState({ favColors: colors });
				});
			}
		}

		_getNormalizedUiControl() {
			const uiControl = this._getUiControl();
			const {
				addButton,
				getCustomColor,
			} = uiControl;
			if (addButton && !getCustomColor) {
				uiControl.getCustomColor = (value) => {
					if (value != null && value.indexOf('customColor') !== -1) {
						return { hexValue: this.props.colorsService.getHexColorFromClassName(value) };
					}
					return null;
				};
			}
			return uiControl;
		}

		_getColorsList() {
			let {
				getColorsList,
				paletteType,
				paletteId,
				ignoreTransparentColor
			} = this._getUiControl();
			if (!getColorsList) {
				getColorsList = this._getColorsListFunction(paletteType, paletteId, ignoreTransparentColor);
			}
			return getColorsList();
		}

		_getColorsListFunction(paletteType, paletteId, ignoreTransparentColor, addMissingBaseColors) {
			let getColorsList = () => [];
			if (paletteType === 'DashboardColorSet') {
				let colorsList = this.props.colorsService.getDashboardColorSet();
				if (ignoreTransparentColor) {
					colorsList = colorsList.filter(color => color.id !== 'transparent');
				}
				getColorsList = () => colorsList.slice(0);
			}
			if (paletteId) {
				if (paletteType !== CONDITIONAL_PALETTE_TYPE) {
					getColorsList = () => {
						return this.props.colorsService.getPaletteColors({
							paletteId,
							paletteType,
							addMissingBaseColors: addMissingBaseColors != null ? addMissingBaseColors : false
						}).then ((colors) => {
							return colors.slice(0);
						});
					};
				} else {
					const type = CONDITIONAL_PALETTE_TYPE;
					getColorsList = () => {
						const paletteColors = [];
						return this.props.colorsService.getPalette({
							paletteId,
							type
						}).then ((condColors) => {
							let fillColors = condColors && condColors.fills || [];
							// color fills are hexValues only, so need to build the color list for them
							if (fillColors) {
								return this.props.colorsService.getPaletteColors({
									paletteId,
									type,
									addMissingBaseColors: true
								}).then ((dbPaletteColors) => {
									// add transparent if not ignored and it isn't already there
									if (!ignoreTransparentColor && !fillColors.includes(NO_COLOR)) {
										let fillColorsTemp = fillColors.slice();
										fillColorsTemp.unshift(NO_COLOR);
										fillColors = fillColorsTemp;
									}
									fillColors.forEach(color => {
										const foundColor = dbPaletteColors.find(paletteColor => paletteColor.label === color);
										if (foundColor) {
											paletteColors.push(foundColor);
										}
									});
									return paletteColors;
								});
							}
							return paletteColors;
						});
					};
				}
			}

			return getColorsList;
		}

		_getColorFromColorList(colors, uiControl) {
			let color;
			if (colors.length) {
				const colorValue = this.props.item.value;
				color = this._getColorFromColorIndex(colorValue, colors, uiControl) ||
						this._getColorFromColorValue(colorValue, colors) ||
						this._getColorFromCustomColorsList(colorValue, uiControl);

				if (!color || !color.hexValue) {
					color = colors[0];
				}
			}
			return color;
		}

		_getColorFromColorIndex(indexValue, colors, uiControl) {
			const { paletteType, defaultColorIndex } = uiControl;
			if (!paletteType || paletteType !== 'ColorPalette') {
				return null;
			}
			if (indexValue != null) {
				return indexValue < colors.length ? colors[indexValue] : colors[0];
			} else {
				// take the default index color or the first color
				return (defaultColorIndex != null && defaultColorIndex < colors.length) ? colors[defaultColorIndex] : colors[0];
			}
		}

		_getColorFromColorValue(colorValue, colors) {
			return colors.find(color => color.id === colorValue || color.hexValue === colorValue);
		}

		_getColorFromCustomColorsList(colorValue, uiControl) {
			const { getCustomColor } = uiControl;
			return getCustomColor ? getCustomColor(colorValue) : null;
		}

		render() {
			const {
				addButton,
				className,
				disabled = false,
				label,
				paletteTitle,
				showHexValue = false,
				flexOptions,
				displayTooltip
			} = this._getUiControl();

			const itemId = this.props.item && this.props.item.id;

			const containerProps = {
				className: cx(this.props.className, className, {
					'no-tooltip': !displayTooltip
				}),
				'data-property-id': itemId ? itemId : undefined
			};

			const componentClassName = cx({
				'is-transparent': this.state.hexValue === 'transparent'
			});

			return (
				<FlexLayout
					{...containerProps}
					wrap='nowrap'
					justifyContent='space-between'
					direction={flexOptions && flexOptions.direction}
				>
					<Label label={label} />
					<FlexLayout wrap='nowrap'>
						{ showHexValue &&
							<Label label={this._getHexValue()} className={'hexLabel'} />
						}
						<AC_ColorPicker
							className={componentClassName}
							colors={this.state.colors.map(color => color.hexValue)}
							colorTitles={this.state.colors.map(color => color.label)}
							colorsLabel={paletteTitle}
							onColorSelected={this._setSelectedColor}
							paletteService={this.props.paletteService}
							resetColor={this._resetColor}
							disabled={disabled}
							selectedColor={this.state.hexValue}
							// selectedColor={this.state.hexValue !== 'transparent' ? stringResources.get('colorNoFillLabel') : this.state.hexValue }
							showCustomColorButton={addButton}
							favoriteColors={this.state.favColors}
							title={label}
						/>
					</FlexLayout>
				</FlexLayout>
			);
		}

		_setSelectedColor = (hexValue) => {
			let propertyValue = hexValue;
			const colorIndex = this.state.colors.findIndex(item => item.hexValue === hexValue);
			const { paletteType } = this._getUiControl();
			if (colorIndex > -1) {
				if (paletteType === 'ColorPalette') {
					propertyValue = colorIndex;
				} else {
					propertyValue = this.state.colors[colorIndex].id;
				}
			}
			// customColor: set the color hexa value
			const { onChange } = this._getUiControl();
			this.setState({ hexValue }, () => {
				if(onChange) {
					onChange(propertyValue);
				} else if (this.props.item.setPropertyValue) {
					this.props.item.setPropertyValue(propertyValue);
				}
			});
		}

		_resetColor = () => {
			this._setSelectedColor(this._defaultColor);
		}

		_getUiControl() {
			return this.props.item && this.props.item.editor && this.props.item.editor.uiControl;
		}

		_getHexValue() {
			if (!this.state.hexValue || this.state.hexValue === 'transparent') {
				return stringResources.get('colorNoFillLabel');
			}
			return this.state.hexValue.toUpperCase();
		}
	}

	ColorPicker.propTypes = {
		item: PropTypes.shape({
			id: PropTypes.string,
			setPropertyValue: PropTypes.func.isRequired,
			value: PropTypes.string,
			editor: PropTypes.shape({
				uiControl: PropTypes.shape({
					label: PropTypes.string.isRequired,
					paletteTitle: PropTypes.string,
					paletteType: PropTypes.string,
					ignoreTransparentColor: PropTypes.bool,
					getColorsList: PropTypes.func,
					getCustomColor: PropTypes.func,
					onChange: PropTypes.func,
					displayTooltip: PropTypes.bool
				})
			}).isRequired
		}),
		className: PropTypes.string,
		paletteService: PropTypes.object.isRequired,
		colorsService: PropTypes.object.isRequired
	};
	ColorPicker.defaultProps = {};

	return MobxReact.observer(ColorPicker);
});